/**
 * DropdownMenu - Reusable dropdown menu component
 * 
 * Creates dropdown menus similar to the account dropdown.
 * Manages opening/closing, positioning, and click-outside handling.
 */

export class DropdownMenu {
  // Static array to track all dropdown instances
  static instances = [];
  
  constructor(options = {}) {
    this.triggerId = options.triggerId; // Button that triggers the dropdown
    this.menuId = options.menuId; // ID for the menu element
    this.items = options.items || []; // Menu items array
    this.onSelect = options.onSelect || (() => {}); // Callback when item selected
    this.position = options.position || 'bottom-right'; // Positioning
    
    this.isOpen = false;
    this.triggerElement = null;
    this.menuElement = null;
    
    // Register this instance
    DropdownMenu.instances.push(this);
    
    this.init();
  }

  init() {
    // Find or create trigger element
    this.triggerElement = document.getElementById(this.triggerId);
    if (!this.triggerElement) {
      console.error(`Trigger element ${this.triggerId} not found`);
      return;
    }

    // Create menu element
    this.createMenu();
    
    // Add click handler to trigger
    this.triggerElement.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggle();
    });
    
    // Close on click outside
    document.addEventListener('click', (e) => {
      if (this.isOpen && !this.menuElement.contains(e.target) && !this.triggerElement.contains(e.target)) {
        this.close();
      }
    });
    
    // Close on ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });
    
    // Listen for other menus opening
    document.addEventListener('menuOpening', (e) => {
      if (e.detail.menuId !== this.menuId && this.isOpen) {
        this.close();
      }
    });
  }

  createMenu() {
    // Remove existing menu if any
    const existing = document.getElementById(this.menuId);
    if (existing) existing.remove();
    
    this.menuElement = document.createElement('div');
    this.menuElement.id = this.menuId;
    this.menuElement.className = 'fixed hidden bg-gray-900 shadow-2xl z-50';
    // Add backdrop blur for better visibility
    this.menuElement.style.backdropFilter = 'blur(8px)';
    this.menuElement.style.backgroundColor = 'rgba(17, 24, 39, 0.95)'; // gray-900 with slight transparency
    
    // Custom borders
    this.menuElement.style.border = '1px solid rgb(75, 85, 99)';
    
    // Border radius - rounded left corners, square top-right for button connection
    this.menuElement.style.borderTopLeftRadius = '0.5rem';
    this.menuElement.style.borderTopRightRadius = '0';
    this.menuElement.style.borderBottomLeftRadius = '0.5rem';
    this.menuElement.style.borderBottomRightRadius = '0.5rem';
    
    // Padding
    this.menuElement.style.paddingTop = '0.25rem';
    this.menuElement.style.paddingBottom = '0.25rem';
    
    // Create menu items
    this.items.forEach((item, index) => {
      if (item.separator) {
        const separator = document.createElement('div');
        separator.className = 'border-t border-gray-700 my-1';
        this.menuElement.appendChild(separator);
      } else if (item.customHTML) {
        // Custom HTML item (for controls like sliders, inputs)
        const customDiv = document.createElement('div');
        customDiv.className = 'px-4 py-2 text-sm text-gray-300';
        customDiv.innerHTML = item.customHTML;
        
        // Run initialization function if provided
        if (item.onMount) {
          setTimeout(() => item.onMount(customDiv), 0);
        }
        
        this.menuElement.appendChild(customDiv);
      } else {
        const button = document.createElement('button');
        button.className = `w-full text-left px-4 py-2 text-sm ${item.danger ? 'text-red-400' : 'text-gray-300'} ${item.disabled ? 'opacity-50 cursor-default' : 'hover:bg-gray-700'} flex items-center ${item.selected ? 'justify-between bg-gray-700/50' : 'space-x-2'}`;
        
        // Add icon if provided
        if (item.icon) {
          const iconSpan = document.createElement('span');
          iconSpan.innerHTML = item.icon;
          button.appendChild(iconSpan);
        }
        
        const labelSpan = document.createElement('span');
        labelSpan.textContent = item.label;
        button.appendChild(labelSpan);
        
        // Add checkmark if selected
        if (item.selected) {
          const checkmark = document.createElement('span');
          checkmark.innerHTML = `<svg class="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path></svg>`;
          button.appendChild(checkmark);
        }
        
        if (!item.disabled) {
          button.addEventListener('click', () => {
            if (item.action) {
              item.action();
              this.close();
            } else {
              this.selectItem(index);
            }
          });
        }
        
        this.menuElement.appendChild(button);
      }
    });
    
    // Add to body
    document.body.appendChild(this.menuElement);
  }

  updateItems(items) {
    this.items = items;
    this.createMenu();
    if (this.isOpen) {
      this.positionMenu();
    }
  }

  selectItem(index) {
    const item = this.items[index];
    if (item && !item.separator) {
      // Update selected state
      this.items.forEach((i, idx) => {
        if (!i.separator) {
          i.selected = idx === index;
        }
      });
      
      // Recreate menu with updated selection
      this.createMenu();
      
      // Call callback
      this.onSelect(item, index);
      
      // Close menu
      this.close();
    }
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    if (this.isOpen) return;
    
    // Dispatch event to close other menus
    document.dispatchEvent(new CustomEvent('menuOpening', {
      detail: { menuId: this.menuId }
    }));
    
    this.isOpen = true;
    this.menuElement.classList.remove('hidden');
    
    // Style the trigger button to connect with menu
    this.triggerElement.style.backgroundColor = 'rgba(17, 24, 39, 0.95)';
    this.triggerElement.style.backdropFilter = 'blur(8px)';
    this.triggerElement.style.border = '1px solid rgb(75, 85, 99)'; // gray-600
    this.triggerElement.style.borderBottom = 'none'; // Remove bottom border to connect with menu
    this.triggerElement.style.borderTopLeftRadius = '0.5rem';
    this.triggerElement.style.borderTopRightRadius = '0.5rem';
    this.triggerElement.style.borderBottomLeftRadius = '0';
    this.triggerElement.style.borderBottomRightRadius = '0';
    this.triggerElement.style.position = 'relative';
    this.triggerElement.style.zIndex = '51'; // Above the menu (which is z-50)
    
    // Position menu after browser has laid out the content
    // This ensures the menu's width is calculated before positioning
    requestAnimationFrame(() => {
      this.positionMenu();
    });
  }

  close() {
    if (!this.isOpen) return;
    
    this.isOpen = false;
    this.menuElement.classList.add('hidden');
    
    // Remove trigger button styling
    this.triggerElement.style.backgroundColor = '';
    this.triggerElement.style.backdropFilter = '';
    this.triggerElement.style.borderTop = '1px solid transparent';
    this.triggerElement.style.borderLeft = '1px solid transparent';
    this.triggerElement.style.borderRight = '1px solid transparent';
    this.triggerElement.style.borderBottom = '';
    this.triggerElement.style.borderTopLeftRadius = '';
    this.triggerElement.style.borderTopRightRadius = '';
    this.triggerElement.style.borderBottomLeftRadius = '';
    this.triggerElement.style.borderBottomRightRadius = '';
    this.triggerElement.style.position = '';
    this.triggerElement.style.zIndex = '';
  }

  positionMenu() {
    if (!this.triggerElement || !this.menuElement) return;
    
    const triggerRect = this.triggerElement.getBoundingClientRect();
    
    // Position based on setting
    if (this.position === 'bottom-right') {
      // Position menu directly below trigger button
      // Align right edges precisely
      this.menuElement.style.top = `${triggerRect.bottom - 1}px`;
      this.menuElement.style.right = `${window.innerWidth - triggerRect.right}px`;
      this.menuElement.style.left = 'auto';
      
      // Now get menu dimensions after positioning
      const menuRect = this.menuElement.getBoundingClientRect();
      
      // Check if menu goes off bottom and reposition above if needed
      if (menuRect.bottom > window.innerHeight) {
        this.menuElement.style.top = `${triggerRect.top - menuRect.height}px`;
      }
    } else if (this.position === 'bottom-left') {
      // Position menu directly below trigger button
      this.menuElement.style.top = `${triggerRect.bottom - 1}px`;
      this.menuElement.style.left = `${triggerRect.left}px`;
      this.menuElement.style.right = 'auto';
      
      // Now get menu dimensions after positioning
      const menuRect = this.menuElement.getBoundingClientRect();
      
      // Check if menu extends beyond window and adjust
      if (menuRect.right > window.innerWidth) {
        this.menuElement.style.left = `${window.innerWidth - menuRect.width - 8}px`;
        this.menuElement.style.right = 'auto';
      }
      
      // Check if menu goes off bottom and reposition above if needed
      if (menuRect.bottom > window.innerHeight) {
        this.menuElement.style.top = `${triggerRect.top - menuRect.height}px`;
      }
    }
  }

  destroy() {
    if (this.menuElement) {
      this.menuElement.remove();
    }
    
    // Remove from instances array
    const index = DropdownMenu.instances.indexOf(this);
    if (index > -1) {
      DropdownMenu.instances.splice(index, 1);
    }
  }
}

